import {OneListUpdate} from '../models/one-list-update.model';
import {FleetTeamOneList} from '../models/one-list.model';
import {ManyResponse} from '@backend/responses/many.response';
import {SCondition, RequestQueryBuilder} from '@nestjsx/crud-request';
import {Injectable} from '@angular/core';
import {Burly} from 'kb-burly';
import {TorchAPIConfiguration} from '../../../config/api.configuration';
import {HttpClient, HttpHeaders, HttpResponse} from '@angular/common/http';
import {Observable} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';
import {TorchTokenService} from '../../@users/services/torch-token.service';
import {OneListProjectMember} from '../interfaces/one-list-project-member.interface';
import {RogueCaseMeeting} from '../../@crt';

@Injectable({
  providedIn: 'root'
})
export class TorchFleetTeamService {

  constructor(protected config: TorchAPIConfiguration, private httpClient: HttpClient, private tokenService: TorchTokenService) {
  }

  public queryProjects(pageSize: number = 50,
                       currentPage: number = 0,
                       sortDirection: 'ASC' | 'DESC' | '' = 'DESC',
                       sortBy: string = 'id',
                       filters: SCondition | null): Observable<ManyResponse<FleetTeamOneList>> {
    const qb = RequestQueryBuilder.create();

    qb.setPage(currentPage + 1).setLimit(pageSize);

    if (!!sortDirection) {
      qb.sortBy({field: sortBy, order: sortDirection});
    }

    if (!!filters) {
      qb.search(filters);
    }

    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/fleet-team/one-list')
      .appendRawQueryString(qb.query(false))
      .get;


    return this.httpClient.get<ManyResponse<FleetTeamOneList>>(endpoint);
  }

  public findUser(startsWith: string): Observable<string[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/graph/find/name')
      .addQuery('startsWith', startsWith)
      .get;

    return this.tokenService.getGraphToken().pipe(
      switchMap(token => this.httpClient.get<string[]>(endpoint, {headers: {msaltoken: `${token}`}}))
    );
  }


  public getUserProfile(userFullName: string) {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/graph/find/name')
      .addQuery('startsWith', userFullName)
      .addQuery('fullProfile', true)
      .get;

    return this.tokenService.getGraphToken().pipe(
      switchMap(token => this.httpClient.get<{ displayName: string; userPrincipalName: string }[]>(
        endpoint,
        {headers: {msaltoken: `${token}`}})
      )
    );
  }

  public getTeamMembers(project: FleetTeamOneList) {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/fleet-team/one-list/teamMembers/')
      .addSegment(`/${project.id}`)
      .get;

    return this.tokenService.getGraphToken().pipe(
      switchMap(token => this.httpClient.get<OneListProjectMember[]>(
        endpoint,
        {headers: {msaltoken: `${token}`}})
      )
    );
  }

  public findUserByEmailAddress(emailAddress: string) {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/graph/find/emailAddress')
      .addQuery('emailAddress', emailAddress)
      .get;

    return this.tokenService.getGraphToken().pipe(
      switchMap(token => this.httpClient.get<{ displayName: string }>(endpoint, {headers: {msaltoken: `${token}`}}))
    );
  }

  getOneListProjectById(projectId: number) {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/fleet-team/one-list/' + projectId)
      .get;

    return this.httpClient.get<FleetTeamOneList>(endpoint).pipe(
      map(value => Object.assign(new FleetTeamOneList(), value))
    );
  }

  public getDistinctATAs(contains: string): Observable<string[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/fleet-team/one-list')
      .addSegment('/ata')
      .addQuery('contains', contains)
      .get;

    return this.httpClient.get<string[]>(endpoint);
  }

  public getDistinctSponsors(contains: string): Observable<string[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/fleet-team/one-list')
      .addSegment('/sponsor')
      .addQuery('contains', contains)
      .get;

    return this.httpClient.get<string[]>(endpoint);
  }

  public getDistinctJustifications(contains: string): Observable<string[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/fleet-team/one-list')
      .addSegment('/justifications')
      .addQuery('contains', contains)
      .get;

    return this.httpClient.get<string[]>(endpoint);
  }

  public getDistinctFleets(contains: string): Observable<string[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/fleet-team/one-list')
      .addSegment('/fleet')
      .addQuery('contains', contains)
      .get;

    return this.httpClient.get<string[]>(endpoint);
  }

  getProjectUpdates(projectId: number) {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/fleet-team/project-updates')
      .addQuery('filter', 'projectId||$eq||' + projectId)
      .get;

    return this.httpClient.get<ManyResponse<OneListUpdate>>(endpoint).pipe(
      map(response => response.data)
    );
  }

  public addProjectUpdate(update: OneListUpdate) {
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/fleet-team/project-updates')
      .get;

    return new Promise((resolve, reject) => {
      this.httpClient.post(endpointURL, update)
        .subscribe((response) => {
          resolve(response);
        }, reject);
    });
  }

  public deleteProjectUpdate(update: OneListUpdate) {
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/fleet-team/project-updates')
      .addSegment('/' + update.id)
      .get;

    return new Promise((resolve, reject) => {
      this.httpClient.delete(endpointURL)
        .subscribe((response) => {
          resolve(response);
        }, reject);
    });
  }

  updateProject(project: FleetTeamOneList): Observable<FleetTeamOneList> {
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/fleet-team/one-list')
      .addSegment('/' + project.id)
      .get;

    return this.httpClient.patch<FleetTeamOneList>(endpointURL, project).pipe(
      map(value => Object.assign(new FleetTeamOneList(), value))
    );
  }

  createProject(project: FleetTeamOneList): Observable<FleetTeamOneList> {
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/fleet-team/one-list')
      .get;

    return this.httpClient.post<FleetTeamOneList>(endpointURL, project).pipe(
      map(value => Object.assign(new FleetTeamOneList(), value))
    );
  }

  public exportDataToExcel(sortDirection: 'ASC' | 'DESC' | '' = 'DESC',
                           sortBy: string = 'id',
                           filters: SCondition | null) {

    const qb = RequestQueryBuilder.create();

    if (!!sortDirection) {
      qb.sortBy({field: sortBy, order: sortDirection});
    }

    if (!!filters) {
      qb.search(filters);
    }

    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/fleet-team/one-list/export')
      .appendRawQueryString(qb.query(false))
      .get;


    const httpOptions: { headers; observe; responseType; } = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      observe: 'response',
      responseType: 'blob'
    };

    return this.httpClient.get(endpoint, httpOptions).pipe(
      map(response => (response as unknown as HttpResponse<any>).body)
    );
  }
}
